import React, { useCallback, useMemo } from 'react';

import {
  BottomSheetContainer,
  Colors,
  createUseBottomSheetModal,
  Flex,
  HEADER_HEIGHT,
  IconCheck,
  IconCoupon,
  IconDownload,
  List,
  PrimaryButton,
  Spinner,
  Typography,
  useScreenSize,
  VerticalSpacing,
} from '@banhala/ui-kit';
import { useMarketCouponList } from '../queries/useMarketCouponList';
import { localizeNumber } from 'shared/src/utils';
import { useOnPressRedeemCoupon } from './useOnPressRedeemCoupon';
import { useOnPressMarketCouponRedeemAll } from './useOnPressMarketCouponRedeemAll';
import type {
  Coupon,
  CouponListSection,
} from 'shared/src/api/marketService/getMarketCouponList';

const CouponItem: React.FC<{ coupon: Coupon; marketId: number }> = props => {
  const onPressRedeemCoupon = useOnPressRedeemCoupon(props.marketId);

  const title = useMemo(() => {
    if (props.coupon.discount_type === '0') {
      return `${localizeNumber(props.coupon.discount_price ?? 0)}원`;
    }

    if (props.coupon.discount_type === '1') {
      return `${props.coupon.discount_percent ?? 0}%`;
    }

    return '';
  }, [
    props.coupon.discount_percent,
    props.coupon.discount_price,
    props.coupon.discount_type,
  ]);

  return (
    <Flex.Row
      padding={{ horizontal: 24, vertical: 24 }}
      shadowStep={'medium'}
      backgroundColor={'white'}
      margin={{ horizontal: 16 }}
      borderRadius={8}
      gap={16}
    >
      <Flex flex={1}>
        <Typography type={'h2'} color={'content_blue_primary'}>
          {title}
        </Typography>
        <VerticalSpacing size={8} />
        <Typography type={'subtitle2'}>{props.coupon.name}</Typography>
        <VerticalSpacing size={12} />
        <Flex gap={4}>
          {props.coupon.conditions.split('\n').map((condition, index) => (
            <Typography
              key={`${condition}-${index}`}
              color={'content_tertiary'}
              type={'body4'}
            >
              {condition}
            </Typography>
          ))}
        </Flex>
      </Flex>
      <Flex alignItems={'center'} justifyContent={'center'}>
        {props.coupon.is_applied ? (
          <Flex
            gap={4}
            alignItems={'center'}
            justifyContent={'center'}
            width={52}
          >
            <IconCheck width={24} height={24} fill={Colors.content_tertiary} />
            <Typography type={'subtitle6'} color={'content_tertiary'}>
              발급완료
            </Typography>
          </Flex>
        ) : (
          <Flex
            borderRadius={26}
            width={52}
            height={52}
            alignItems={'center'}
            justifyContent={'center'}
            backgroundColor={'blue5'}
            onPress={() => onPressRedeemCoupon.action(props.coupon)}
          >
            {onPressRedeemCoupon.loading ? (
              <Spinner size={24} color={'content_blue_primary'} />
            ) : (
              <IconDownload
                width={24}
                height={24}
                fill={Colors.content_blue_primary}
              />
            )}
          </Flex>
        )}
      </Flex>
    </Flex.Row>
  );
};

const EmptyCouponList = () => {
  const { height } = useScreenSize();

  return (
    <Flex
      height={height * 0.8 - HEADER_HEIGHT}
      alignItems={'center'}
      justifyContent={'center'}
    >
      <Flex alignItems={'center'} gap={12}>
        <IconCoupon width={52} height={52} color={Colors.content_secondary} />
        <Typography type={'subtitle1'} color={'content_secondary'}>
          쿠폰을 모두 다운받았어요
        </Typography>
      </Flex>
    </Flex>
  );
};

const CouponListView: React.FC<{ marketId: number }> = props => {
  const { data: marketCouponList } = useMarketCouponList(props.marketId);
  const onPressMarketCouponRedeemAll = useOnPressMarketCouponRedeemAll(
    props.marketId,
  );

  const totalCoupons = useMemo(() => {
    return marketCouponList.sections.reduce<Coupon[]>(
      (acc, section) => acc.concat(section.coupons),
      [],
    );
  }, [marketCouponList.sections]);

  const isAllCouponIssued = useMemo(() => {
    return totalCoupons.every(coupon => coupon.is_applied);
  }, [totalCoupons]);

  const onPressBottomRedeemAllCoupon = useCallback(() => {
    if (isAllCouponIssued) return;

    onPressMarketCouponRedeemAll.action();
  }, [isAllCouponIssued, onPressMarketCouponRedeemAll]);

  return (
    <Flex flex={1}>
      <List<CouponListSection>
        data={marketCouponList.sections}
        ListEmptyComponent={EmptyCouponList}
        contentContainerStyle={{ paddingTop: 12, paddingBottom: 12 }}
        renderItem={({ item: section }) => {
          return (
            <Flex>
              <Flex padding={{ horizontal: 16, vertical: 12 }}>
                <Typography type={'subtitle1'} color={'content_secondary'}>
                  {section.title}
                </Typography>
              </Flex>
              <List<Coupon>
                data={section.coupons}
                ItemSeparatorComponent={() => <VerticalSpacing size={16} />}
                renderItem={({ item: coupon }) => (
                  <CouponItem coupon={coupon} marketId={props.marketId} />
                )}
              />
            </Flex>
          );
        }}
      ></List>
      {marketCouponList.sections.length > 0 && (
        <BottomButton
          isAllIssued={isAllCouponIssued}
          onPress={onPressBottomRedeemAllCoupon}
          loading={onPressMarketCouponRedeemAll.loading}
        />
      )}
    </Flex>
  );
};

const BottomButton: React.FC<{
  isAllIssued: boolean;
  loading?: boolean;
  onPress: () => void;
}> = props => {
  return (
    <Flex
      padding={{ horizontal: 16, vertical: 16 }}
      borderTopWidth={1}
      borderColor={'border_tertiary'}
    >
      <PrimaryButton
        color="blue"
        outline
        size="xlarge"
        variant={'fill'}
        leftIcon={props.isAllIssued ? undefined : <IconDownload />}
        onPress={props.onPress}
        state={
          props.isAllIssued ? 'disabled' : props.loading ? 'loading' : undefined
        }
      >
        {props.isAllIssued ? '다운로드 완료' : '모두 다운받기'}
      </PrimaryButton>
    </Flex>
  );
};

export const useShowMarketCouponListBottomSheet = createUseBottomSheetModal<
  void,
  { marketId: number }
>(({ param, confirm }) => {
  const { height } = useScreenSize();

  const contentHeight = height * 0.8;

  return (
    <BottomSheetContainer title={'쿠폰'} onPressClose={confirm}>
      <React.Suspense
        fallback={
          <Flex height={contentHeight}>
            <Flex flex={1} alignItems={'center'} justifyContent={'center'}>
              <Spinner color={'content_blue_primary'} />
            </Flex>
            <BottomButton isAllIssued={false} onPress={() => {}} />
          </Flex>
        }
      >
        <Flex height={contentHeight}>
          <CouponListView marketId={param.marketId} />
        </Flex>
      </React.Suspense>
    </BottomSheetContainer>
  );
});
